import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi, getAccessToken, apiFetchAuthed } from '../apiHelpers';
import config from '../config';
import { instance } from '../instance';
import useUserStore from '../stores/useUserStore';
import AnalysisChat from './AnalysisChat';
import AnalysisWorkspace from './AnalysisWorkspace';
import PreAnalysisPanel from './panels/PreAnalysisPanel';
import Explorer from './explorer/Explorer';
import useLogData from './explorer/useLogData';
import MemoriesModal from './MemoriesModal';
import { MONO, FOCUS, MOTION, SkeletonRows, CloseIcon } from './styles';
import {
  ChatMessage, AgentHealth, ProgrammaticResult,
  Severity,
} from './types';
import { AgentChart, ViewContext } from './explorer/layoutTypes';
import { IconButton, buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import ChipToggle from '../components/ui/ChipToggle';
import { Field } from '../components/ui/Field';
import SegTrack from '../components/ui/SegTrack';
import Tabs from '../components/ui/Tabs';
import Alert from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Popover from '../components/ui/Popover';
import EmptyState from '../components/ui/EmptyState';
import { ListRow, ListStack } from '../components/ui/ListRow';

const newThreadId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID()
  : `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

/* ── Session List Panel ── */
const SessionListPanel: React.FC<{
  api: any;
  onLoad: (session: any) => void;
  onClose: () => void;
  currentSessionId: number | null;
  anchor: React.RefObject<HTMLElement | null>;
}> = ({ api, onLoad, onClose, currentSessionId, anchor }) => {
  const [tab, setTab] = useState<'mine' | 'global'>('mine');
  const [mySessions, setMySessions] = useState<any[]>([]);
  const [sharedSessions, setSharedSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let done = 0;
    const check = () => { done++; if (done >= 2) setLoading(false); };
    api.get('/analysis/sessions', {
      onSuccess: (data: any) => { setMySessions(Array.isArray(data) ? data : []); check(); },
      onError: check,
    });
    api.get('/analysis/sessions/shared', {
      onSuccess: (data: any) => { setSharedSessions(Array.isArray(data) ? data : []); check(); },
      onError: check,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleShare = (sessionId: number, currentlyShared: boolean) => {
    api.patch(`/analysis/sessions/${sessionId}`, {
      body: JSON.stringify({ shared: !currentlyShared }),
      onSuccess: () => {
        setMySessions(prev => prev.map(s => s.id === sessionId ? { ...s, shared: !currentlyShared } : s));
        if (!currentlyShared) {
          // Refresh shared list
          api.get('/analysis/sessions/shared', {
            onSuccess: (data: any) => setSharedSessions(Array.isArray(data) ? data : []),
          });
        } else {
          setSharedSessions(prev => prev.filter(s => s.id !== sessionId));
        }
      },
    });
  };

  const sessions = tab === 'mine' ? mySessions : sharedSessions;

  return (
    <Popover open anchor={anchor} onClose={onClose} title="Saved sessions" side="bottom" align="end" width="lg">
      {/* Tabs */}
      <div className="flex items-center gap-1 pb-2">
        <Tabs
          label="Saved sessions"
          fill
          size="sm"
          className="min-w-0 flex-1"
          value={tab}
          onChange={(key) => setTab(key as typeof tab)}
          tabs={[
            { key: 'mine', label: 'Mine', count: mySessions.length },
            { key: 'global', label: 'Shared', count: sharedSessions.length },
          ]}
        />
      </div>

      {/* Session list */}
      {loading ? (
        <SkeletonRows rows={4} />
      ) : sessions.length === 0 ? (
        <EmptyState variant="area">
          {tab === 'mine'
            ? 'No saved sessions yet. Ask the analyst a question and the session saves itself.'
            : 'Nothing shared yet. Share one of your sessions to list it here.'}
        </EmptyState>
      ) : (
        <ListStack
          items={sessions}
          getKey={(s: any) => String(s.id)}
          on="panel"
          label="Saved sessions"
          renderRow={(s: any) => (
            <ListRow
              selected={s.id === currentSessionId}
              onClick={() => {
                api.get(`/analysis/sessions/${s.id}`, {
                  onSuccess: (full: any) => onLoad(full),
                });
              }}
              actions={tab === 'mine' ? (
                /* Share toggle (only on my sessions) */
                <Tooltip label={s.shared ? 'Everyone with analyst access can open this. Select to stop sharing.' : 'Let everyone with analyst access open this session'}>
                  <ChipToggle
                    size="sm"
                    checked={!!s.shared}
                    onChange={() => toggleShare(s.id, s.shared)}
                    labelProps={{ onClick: (e) => e.stopPropagation() }}
                    className="flex-shrink-0"
                  >
                    {s.shared ? 'Shared' : 'Share'}
                  </ChipToggle>
                </Tooltip>
              ) : undefined}
            >
              <span className="block min-w-0">
                <span className="block truncate text-sm text-on-surface">
                  {s.title || 'Untitled session'}
                </span>
                <span className={`mt-0.5 flex min-w-0 gap-3 text-xs text-on-surface-variant ${MONO} tabular-nums`}>
                  {s.drone_serial && <span className="truncate">{s.drone_serial}</span>}
                  <span className="whitespace-nowrap">{new Date(s.updated_at).toLocaleDateString()}</span>
                  {tab === 'global' && s.firstname && (
                    <span className="truncate font-sans">{s.firstname} {s.lastname?.[0]}.</span>
                  )}
                </span>
                {s.summary && (
                  <span className="mt-1 block truncate text-xs text-on-surface-variant">
                    {s.summary.slice(0, 100)}
                  </span>
                )}
              </span>
            </ListRow>
          )}
        />
      )}
    </Popover>
  );
};

const AnalysisPage: React.FC = () => {
  const history = useHistory();
  const api = useApi();
  const user = useUserStore((s) => s.user);

  // Auth gate
  const canAccess = user.can_admin || user.can_analyst;

  // --- sessionStorage persistence (survives refresh) ---
  const STORAGE_KEY = 'analyst_session';

  const loadSession = (): any => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  };

  const saved = useRef(loadSession());

  // State — initialize from sessionStorage if available
  const [logId, setLogId] = useState(saved.current.logId || '');
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    return (saved.current.messages || []).map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) }));
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentToolCall, setCurrentToolCall] = useState<string>('');
  const [agentHealth, setAgentHealth] = useState<AgentHealth>({ reachable: false });
  const [showMemories, setShowMemories] = useState(false);
  const [programmaticResult, setProgrammaticResult] = useState<ProgrammaticResult | null>(saved.current.programmaticResult || null);
  const [comparisonResult, setComparisonResult] = useState<any>(saved.current.comparisonResult || null);
  const [overallSeverity, setOverallSeverity] = useState<Severity | undefined>(saved.current.overallSeverity);
  const [chatWidth, setChatWidth] = useState(saved.current.chatWidth || 380);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const [selectedSerials, setSelectedSerials] = useState<Record<string, string>>({});
  const [timeWindow, setTimeWindow] = useState<{ start: number; end?: number } | null>(null);
  const [promptInput, setPromptInput] = useState('');
  const [agentCharts, setAgentCharts] = useState<AgentChart[]>([]);
  // Shared focus state: connects charts ↔ 3D viewer ↔ chat highlights
  // Throttled to avoid re-render cascade on every mousemove
  const [focusTime, setFocusTime] = useState<number | null>(null);
  const focusRafRef = useRef<number>(0);
  const throttledSetFocusTime = useCallback((t: number | null) => {
    cancelAnimationFrame(focusRafRef.current);
    focusRafRef.current = requestAnimationFrame(() => setFocusTime(t));
  }, []);
  // DB session persistence
  const [sessionId, setSessionId] = useState<number | null>(saved.current.sessionId || null);
  // The discussion thread on the analyst box. One per investigation; the box keeps
  // the evidence, the page keeps the id. A new log or a new analysis starts a new thread.
  const [threadId, setThreadId] = useState<string>(saved.current.threadId || newThreadId());
  const [showSessionList, setShowSessionList] = useState(false);
  const sessionsKeyRef = useRef<HTMLButtonElement>(null);
  const [modelMode, setModelMode] = useState<'smart' | 'fast'>('smart');

  // One log's overview and report, shared by the explorer and the pre-analysis panel.
  const singleLogId = logId.includes(',') ? '' : logId;
  const logData = useLogData(api, singleLogId);
  const loadedInfo = logData.charts?.info;
  useEffect(() => {
    if (loadedInfo?.serial) {
      setProgrammaticResult(prev => prev
        ? { ...prev, drone_serial: loadedInfo.serial }
        : { drone_serial: loadedInfo.serial } as any
      );
    }
  }, [loadedInfo]);

  // What the explorer shows, kept in a ref so a hover or zoom never re-renders the page.
  const viewRef = useRef<ViewContext | null>(null);
  const handleViewChange = useCallback((view: ViewContext) => { viewRef.current = view; }, []);

  // Clear stale state when switching logs
  const handleLogIdChange = useCallback((newId: string) => {
    if (newId !== logId) setFocusTime(null);
    if (newId !== logId && messages.length > 0) {
      setMessages([]);
      setAgentCharts([]);
      setSessionId(null);
      setThreadId(newThreadId());
      setComparisonResult(null);
    }
    setLogId(newId);
  }, [logId, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const draggingRef = useRef(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Rate limit countdown ticker
  useEffect(() => {
    if (rateLimitCountdown > 0) {
      countdownRef.current = setInterval(() => {
        setRateLimitCountdown(prev => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
    }
  }, [rateLimitCountdown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save to sessionStorage on state changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        logId, messages, programmaticResult, comparisonResult, overallSeverity, chatWidth, sessionId, threadId,
      }));
    } catch { /* quota exceeded — ignore */ }
  }, [logId, messages, programmaticResult, comparisonResult, overallSeverity, chatWidth, sessionId, threadId]);

  // Auto-save session to DB after each assistant message
  const lastMessageCountRef = useRef(messages.length);
  useEffect(() => {
    const assistantMessages = messages.filter(m => m.role === 'assistant');
    if (assistantMessages.length === 0 || messages.length <= lastMessageCountRef.current) {
      lastMessageCountRef.current = messages.length;
      return;
    }
    lastMessageCountRef.current = messages.length;

    const workspaceState = {
      messages, logId, agentCharts, threadId,
      programmaticResult, comparisonResult, overallSeverity,
    };
    const lastAssistant = assistantMessages[assistantMessages.length - 1];
    const title = logId
      ? `${programmaticResult?.drone_serial || logId.slice(0, 8)}, ${new Date().toLocaleDateString()}`
      : `Analysis, ${new Date().toLocaleDateString()}`;

    if (sessionId) {
      // Update existing session
      api.patch(`/analysis/sessions/${sessionId}`, {
        body: JSON.stringify({
          workspace_state: workspaceState,
          summary: lastAssistant.content?.slice(0, 500),
        }),
      });
    } else {
      // Create new session
      api.post('/analysis/sessions', {
        body: JSON.stringify({
          title,
          drone_serial: programmaticResult?.drone_serial || null,
          flight_review_log_id: logId.includes(',') ? logId.split(',')[0] : logId,
          workspace_state: workspaceState,
          summary: lastAssistant.content?.slice(0, 500),
        }),
        onSuccess: (data: any) => { if (data?.id) setSessionId(data.id); },
      });
    }
  }, [messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load a saved session
  const loadSavedSession = useCallback((session: any) => {
    const ws = session.workspace_state || {};
    // The setters below commit one at a time, and messages land before the
    // session id does. Tell the auto-save these messages are already saved, or
    // it posts a copy of the session (or patches whichever one was open).
    lastMessageCountRef.current = (ws.messages || []).length;
    setLogId(ws.logId || session.flight_review_log_id || '');
    setMessages((ws.messages || []).map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })));
    setAgentCharts(ws.agentCharts || []);
    setProgrammaticResult(ws.programmaticResult || null);
    setComparisonResult(ws.comparisonResult || null);
    setOverallSeverity(ws.overallSeverity);
    setSessionId(session.id);
    setThreadId(ws.threadId || newThreadId());
    setShowSessionList(false);
  }, []);

  // A link into one saved session (`/analyst?session=<id>`): the Slack intake
  // replies with it, and any shared session can be passed around the same way.
  // Loaded once, then the param is dropped so a refresh keeps the local state.
  const location = useLocation();
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get('session');
    if (!wanted) return;
    api.get(`/analysis/sessions/${wanted}`, {
      onSuccess: (session: any) => { if (session?.id) loadSavedSession(session); },
      errorAlert: 'That analysis could not be opened',
    });
    history.replace(location.pathname);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check agent health — poll every 5s when offline, back off to 30s when healthy
  const checkHealth = useCallback(async () => {
    await api.get('/analysis/agent-health', {
      onSuccess: (data: AgentHealth) => setAgentHealth(data),
      onError: () => setAgentHealth({ reachable: false, error: 'request failed' }),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, agentHealth.reachable ? 30000 : 5000);
    return () => clearInterval(interval);
  }, [checkHealth, agentHealth.reachable]);

  // Page title
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${instance.displayName} Analyst`;
    return () => { document.title = prevTitle; };
  }, []);

  // Divider drag handler
  const onDividerDrag = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startW = chatWidth;

    const onMove = (ev: PointerEvent) => {
      if (!draggingRef.current) return;
      const delta = startX - ev.clientX;
      setChatWidth(Math.max(280, Math.min(600, startW + delta)));
    };
    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [chatWidth]);

  // Send message to agent
  const handleSend = useCallback(async (message: string) => {
    if (!logId) return;

    // Handle compare command — uses SSE to avoid Heroku H12 timeout
    if (message.startsWith('compare:')) {
      const ids = message.slice(8).split(',').map(s => s.trim()).filter(Boolean);
      if (ids.length < 2) return;

      setIsAnalyzing(true);
      setCurrentToolCall('Comparing ' + ids.length + ' logs');

      const controller = new AbortController();
      abortRef.current = controller;

      // Process a single SSE event payload. Returns true if a `complete` event was handled.
      const processCompareEvent = (eventType: string, dataStr: string): boolean => {
        try {
          const data = JSON.parse(dataStr);
          if (eventType === 'status') {
            setCurrentToolCall(data.message || 'Comparing');
          } else if (eventType === 'complete') {
            setComparisonResult(data);
            return true;
          } else if (eventType === 'error') {
            throw new Error(data.message || 'Comparison failed');
          }
        } catch (parseErr: any) {
          if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
        }
        return false;
      };

      try {
        // Ensure fresh token
        let token = getAccessToken();
        if (!token) {
          try {
            await apiFetchAuthed('/analysis/agent-health', { method: 'GET', silent: true });
            token = getAccessToken();
          } catch { /* ignore */ }
        }

        const response = await fetch(`${config.API_ENDPOINT}/analysis/compare/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({ log_ids: ids }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        // Parse SSE events (accumulate data lines, process on blank line delimiter)
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = '';
        let currentData = '';
        let receivedComplete = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              currentData += (currentData ? '\n' : '') + line.slice(6);
            } else if (line === '' && currentEvent && currentData) {
              // Blank line = end of SSE event
              const processed = processCompareEvent(currentEvent, currentData);
              if (processed) receivedComplete = true;
              currentEvent = '';
              currentData = '';
            }
          }
        }

        // Flush any pending event that wasn't terminated by a trailing blank line
        if (currentEvent && currentData && !receivedComplete) {
          const processed = processCompareEvent(currentEvent, currentData);
          if (processed) receivedComplete = true;
        }

        if (!receivedComplete) {
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            role: 'system' as const,
            content: 'The comparison stopped before it finished, so the results may be incomplete. Run it again to finish.',
            timestamp: new Date(),
          }]);
        } else {
          // Add assistant message so chatActive stays true and the session gets persisted
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            role: 'assistant' as const,
            content: `Compared ${ids.length} logs. The comparison table has the metrics side by side.`,
            timestamp: new Date(),
          }]);
        }
      } catch (err: any) {
        if (err.name === 'AbortError') {
          // User cancelled — no error message needed
        } else {
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            role: 'system' as const,
            content: 'The comparison failed: ' + (err?.message || 'network error') + '. Check the log ids and run it again.',
            timestamp: new Date(),
          }]);
        }
      } finally {
        setIsAnalyzing(false);
        setCurrentToolCall('');
      }
      return;
    }

    // Add user message
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsAnalyzing(true);
    setCurrentToolCall('Connecting to the analyst');

    const toolCallsAccum: any[] = [];
    const startTime = Date.now();

    try {
      const windowSuffix = timeWindow
        ? timeWindow.end != null
          ? `\n\n[User selected time window: T+${Math.floor(timeWindow.start / 60)}:${String(Math.floor(timeWindow.start % 60)).padStart(2, '0')} to T+${Math.floor(timeWindow.end / 60)}:${String(Math.floor(timeWindow.end % 60)).padStart(2, '0')} (${Math.round(timeWindow.start)}s - ${Math.round(timeWindow.end)}s). Focus on this time range first, expand if needed.]`
          : `\n\n[User selected start point: T+${Math.floor(timeWindow.start / 60)}:${String(Math.floor(timeWindow.start % 60)).padStart(2, '0')} (${Math.round(timeWindow.start)}s) with no end point. Analyze from this point to the end of the log.]`
        : '';

      // Refresh token first (ensures we have a valid JWT)
      let token = getAccessToken();
      if (!token) {
        try {
          // Trigger a token refresh by making a lightweight authed call
          await apiFetchAuthed('/analysis/agent-health', { method: 'GET', silent: true });
          token = getAccessToken();
        } catch { /* ignore */ }
      }
      // Token refresh done

      // Direct fetch for SSE — apiFetchAuthed doesn't work with streams
      const controller = new AbortController();
      abortRef.current = controller;
      const response = await fetch(`${config.API_ENDPOINT}/analysis/ask/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          // For multi-log: send first log as primary, mention others in the question
          log_id: logId.includes(',') ? logId.split(',')[0].trim() : logId,
          question: logId.includes(',')
            ? `${message}${windowSuffix}\n\nIMPORTANT: The user selected ${logId.split(',').length} logs to compare. All log IDs: ${logId.split(',').map(s => s.trim()).join(', ')}. You MUST call compare_logs with log_ids=[${logId.split(',').map(s => `"${s.trim()}"`).join(', ')}] to do a side-by-side analysis.`
            : message + windowSuffix,
          drone_serial: programmaticResult?.drone_serial,
          view: logId.includes(',') ? undefined : viewRef.current ?? undefined,
          model_mode: modelMode,
          thread_id: threadId,
          // Only read by the box when it does not know this thread (restart, expiry):
          // the text of the last turns, so the discussion can resume without its evidence.
          history: messages
            .filter(m => m.role === 'user' || m.role === 'assistant')
            .slice(-12)
            .map(m => ({ role: m.role, content: m.content })),
        }),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response stream');

      const decoder = new TextDecoder();
      let buffer = '';
      // Persistent across chunks — an event can span multiple chunks
      let eventType = '';
      let eventData = '';
      let receivedComplete = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse complete lines from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData += (eventData ? '\n' : '') + line.slice(6);
          } else if (line === '' && eventType && eventData) {
            // Complete event — process it
            try {
              const data = JSON.parse(eventData);
              switch (eventType) {
                case 'status':
                  setCurrentToolCall(data.message || '');
                  if (data.drone_serial) {
                    setProgrammaticResult(prev => prev
                      ? { ...prev, drone_serial: data.drone_serial }
                      : { drone_serial: data.drone_serial } as any
                    );
                  }
                  break;
                case 'iteration':
                  setRateLimitCountdown(0);
                  // Don't show "Iteration N/M" — the tool_call events are more informative
                  break;
                case 'tool_call':
                  setCurrentToolCall(`${data.name}(${Object.values(data.params || {}).join(', ').slice(0, 60)})`);
                  break;
                case 'tool_result':
                  toolCallsAccum.push({ tool_name: data.name, response_size: data.chars, duration_ms: data.ms });
                  setCurrentToolCall(`${data.name} \u2192 ${(data.chars || 0).toLocaleString()} chars`);
                  break;
                case 'rate_limit':
                  setRateLimitCountdown(data.wait_s || 60);
                  setCurrentToolCall(`Rate limited (attempt ${data.attempt || '?'}/${data.max_attempts || '?'})`);
                  break;
                case 'api_stats':
                  setCurrentToolCall(`API: ${(data.input_tokens || 0).toLocaleString()} in, ${(data.output_tokens || 0).toLocaleString()} out`);
                  break;
                case 'chart':
                  // Agent rendered a chart — add to workspace (highlights are per-chart)
                  setAgentCharts(prev => [...prev, data]);
                  break;
                case 'complete': {
                  receivedComplete = true;
                  const elapsed = (Date.now() - startTime) / 1000;
                  const assistantMsg: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: data.response || data.summary || 'Analysis complete.',
                    timestamp: new Date(),
                    toolCalls: data.tool_calls || toolCallsAccum,
                    usage: data.usage,
                    iterations: data.iterations,
                    duration_s: elapsed,
                  };
                  setMessages(prev => [...prev, assistantMsg]);
                  if (data.drone_serial) {
                    setProgrammaticResult(prev => prev
                      ? { ...prev, drone_serial: data.drone_serial }
                      : { drone_serial: data.drone_serial } as any
                    );
                  }
                  break;
                }
                case 'error':
                  setMessages(prev => [...prev, {
                    id: (Date.now() + 1).toString(),
                    role: 'system' as const,
                    content: 'The analyst hit an error: ' + (data.message || data.type || 'unknown') + '. Ask again, or start a new analysis.',
                    timestamp: new Date(),
                  }]);
                  break;
              }
            } catch {
              // Skip malformed events
            }
            eventType = '';
            eventData = '';
          } else if (line.startsWith(':')) {
            // SSE comment (keepalive) — ignore
          }
        }
      }
      // Stream ended without a complete event — agent may have crashed
      if (!receivedComplete) {
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'system' as const,
          content: 'The analyst stopped answering mid-reply, so this answer may be incomplete. Ask again to finish it.',
          timestamp: new Date(),
        }]);
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'system' as const,
          content: 'You stopped the analysis.',
          timestamp: new Date(),
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'system' as const,
          content: 'Could not reach the analyst: ' + (err?.message || 'network error') + '. Check that it reads Online, then ask again.',
          timestamp: new Date(),
        }]);
      }
    } finally {
      abortRef.current = null;
      setIsAnalyzing(false);
      setCurrentToolCall('');
    }
  }, [logId, programmaticResult, timeWindow, modelMode, messages, threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop analysis
  const handleStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  // Full analyze (chat "Analyze this flight log" shortcut)
  const handleAnalyze = useCallback((question?: string) => {
    // Check if logId contains commas (multi-log from selector)
    if (logId.includes(',')) {
      handleSend(`compare:${logId}`);
      return;
    }

    if (question) {
      handleSend(question);
    } else {
      handleSend('Analyze this flight log. Tell me everything noteworthy — issues, anomalies, anything interesting.');
    }
  }, [handleSend, logId]);

  // Auth gate render
  if (!canAccess) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-2 bg-surface px-6 text-center">
        <h1 className="text-lg font-semibold text-on-surface">You do not have access to the analyst</h1>
        <p className="max-w-sm text-sm text-on-surface-variant">
          Ask an admin to give your account analyst access.
        </p>
        <BackLink to="/" label="Dashboard" className="mt-3" />
      </div>
    );
  }

  // Chat is active once the user starts analyzing (has messages or is mid-analysis)
  const chatActive = messages.length > 0 || isAnalyzing;

  // An unreachable agent always carries `error`, so neither field means the first check is still out.
  const agentChecked = agentHealth.reachable || agentHealth.error !== undefined;
  const statusText = !agentChecked
    ? 'Checking'
    : agentHealth.reachable
      ? (isAnalyzing ? 'Analyzing' : 'Online')
      : 'Agent offline, retrying';
  const statusDot = !agentChecked
    ? 'ring-1 ring-inset ring-on-surface-variant'
    : agentHealth.reachable
      ? (isAnalyzing ? 'ring-2 ring-inset ring-primary' : 'bg-emerald-400')
      : 'bg-red-400';

  const firmwareBranch = programmaticResult?.firmware_branch;
  const headerSerial = chatActive ? programmaticResult?.drone_serial : undefined;

  const formatT = (s: number) => `T+${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div className="flex min-h-dvh flex-col bg-surface text-on-surface md:h-dvh md:overflow-hidden">
      {/* Header */}
      <header className="flex flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 md:flex-nowrap">
        <div className="flex min-w-0 flex-1 items-baseline gap-3">
          <h1 className="text-base font-semibold text-on-surface">Analyst</h1>
          {(headerSerial || firmwareBranch) && (
            <span className={`flex min-w-0 gap-3 text-xs text-on-surface-variant ${MONO}`}>
              {headerSerial && <span className="truncate">{headerSerial}</span>}
              {firmwareBranch && <span className="truncate">firmware {firmwareBranch}</span>}
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-1">
          {/* Agent health */}
          <div role="status" className="mr-2 flex items-center gap-2 whitespace-nowrap text-xs text-on-surface-variant">
            <span aria-hidden="true" className={`h-2 w-2 flex-shrink-0 rounded-full ${statusDot}`} />
            {statusText}
          </div>

          {/* Model toggle: only where the box reports a second model */}
          {agentHealth.model_fast !== null && (
            <SegTrack
              label="Model"
              size="sm"
              className="mr-1"
              value={modelMode}
              onChange={(key) => setModelMode(key as typeof modelMode)}
              options={[
                { key: 'smart', label: 'Deep', hint: 'Deep thinks longer on hard questions' },
                { key: 'fast', label: 'Quick', hint: 'Quick answers faster on simple questions' },
              ]}
            />
          )}

          {/* Sessions */}
          <button
            ref={sessionsKeyRef}
            type="button"
            onClick={() => setShowSessionList(!showSessionList)}
            aria-expanded={showSessionList}
            className={`${buttonClass('tonal', 'sm')} ${showSessionList ? 'ring-1 ring-primary/40' : ''}`}
          >
            Sessions
          </button>

          {/* Reference flights: vouch for this flight as a good (or acceptable, or bad)
              example. The server refuses "good" unless the programmatic pass was clean. */}
          {logId && !logId.includes(',') && (
            <Tooltip label="Tag this flight as a reference for its firmware, or remove its tag">
              <button
                type="button"
                onClick={async () => {
                  const raw = window.prompt(
                    'Tag this flight as a reference. Type a label and a note, like:\n  good: clean show, calm wind\n  bad: ESC 3 desync at 40 s\n  none: no longer a reference, found a sag on re-read',
                  );
                  if (!raw) return;
                  const m = raw.match(/^\s*(good|acceptable|bad|none)\s*:\s*(.+)$/i);
                  if (!m) { window.alert('Start with good, acceptable, bad or none, then a colon and a note.'); return; }
                  const label = m[1].toLowerCase() === 'none' ? null : m[1].toLowerCase();
                  const pendingId = Date.now().toString();
                  if (label) {
                    setMessages(prev => [...prev, {
                      id: pendingId, role: 'system' as const, timestamp: new Date(),
                      content: 'Screening the flight on the analyst before tagging (the programmatic pass, no model call)...',
                    }]);
                  }
                  try {
                    const r = await apiFetchAuthed('/analysis/reference', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ flight_review_log_id: logId, label, note: m[2].trim() }),
                    });
                    const body = await r.json();
                    setMessages(prev => prev.filter(x => x.id !== pendingId));
                    if (!r.ok) { window.alert(body.error || `Could not tag this flight (${r.status}). Try again.`); return; }
                    setMessages(prev => [...prev, {
                      id: Date.now().toString(), role: 'system' as const, timestamp: new Date(),
                      content: label
                        ? `Tagged as a ${label} reference flight by ${body.labeled_by || 'you'} (screened ${body.severity}, firmware ${body.firmware_branch}).`
                        : 'Reference tag removed.',
                    }]);
                  } catch (err: any) {
                    window.alert('Could not tag this flight: ' + (err?.message || 'network error') + '. Try again.');
                  }
                }}
                className={buttonClass('tonal', 'sm')}
              >
                Reference
              </button>
            </Tooltip>
          )}

          <button type="button" onClick={() => setShowMemories(true)} className={buttonClass('tonal', 'sm')}>
            Knowledge
          </button>

          {/* New Analysis */}
          <Appear show={messages.length > 0 || !!programmaticResult}>
            <button
              type="button"
              onClick={() => {
                setLogId('');
                setMessages([]);
                setProgrammaticResult(null);
                setComparisonResult(null);
                setOverallSeverity(undefined);
                setIsAnalyzing(false);
                setCurrentToolCall('');
                setRateLimitCountdown(0);
                setTimeWindow(null);
                setSelectedSerials({});
                setPromptInput('');
                setAgentCharts([]);
                setFocusTime(null);
                setSessionId(null);
                setThreadId(newThreadId());
                sessionStorage.removeItem(STORAGE_KEY);
              }}
              className={buttonClass('tonal', 'sm')}
            >
              New analysis
            </button>
          </Appear>

          {/* Close */}
          <Tooltip label="Back to dashboard">
            <IconButton label="Back to dashboard" onClick={() => history.push('/')}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
      </header>

      {/* Main content */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 px-2 pb-2 md:flex-row md:gap-0">
        {/* Workspace */}
        <AnalysisWorkspace
          logId={logId}
          onLogIdChange={handleLogIdChange}
          onAnalyze={handleAnalyze}
          onTimeWindowSelect={(start_s, end_s) => {
            setTimeWindow({ start: start_s, end: end_s });
          } }
          onSerialsChange={setSelectedSerials}
          isAnalyzing={isAnalyzing}
          hasMessages={messages.length > 0}
          programmaticResult={programmaticResult}
          comparisonResult={comparisonResult}
          overallSeverity={overallSeverity}
          focusTime={focusTime}
        />

        {/* Explorer */}
        <div className="flex min-h-[60dvh] min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-surface-container-low md:ml-2 md:min-h-0">
          <Explorer
            logId={singleLogId}
            logCharts={logData.charts}
            logChartsLoading={logData.chartsLoading}
            report={logData.report}
            agentCharts={agentCharts}
            focusTime={focusTime}
            onFocusTimeChange={throttledSetFocusTime}
            onViewChange={handleViewChange}
          />
        </div>

        {/* Resizable divider: drag it, or focus it and use the arrow keys */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Chat width, ${chatWidth} pixels. Arrow keys resize.`}
          tabIndex={0}
          onPointerDown={onDividerDrag}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 80 : 24;
            if (e.key === 'ArrowLeft') { setChatWidth(w => Math.max(280, w - step)); e.preventDefault(); }
            else if (e.key === 'ArrowRight') { setChatWidth(w => Math.min(600, w + step)); e.preventDefault(); }
            else if (e.key === 'Home') { setChatWidth(280); e.preventDefault(); }
            else if (e.key === 'End') { setChatWidth(600); e.preventDefault(); }
          }}
          className={`group hidden w-2 flex-shrink-0 cursor-col-resize touch-none items-center justify-center rounded hover:bg-surface-container-high active:bg-surface-container-high md:flex ${FOCUS} ${MOTION}`}
        >
          <span className={`h-8 w-0.5 rounded-full bg-outline-variant group-hover:bg-on-surface-variant ${MOTION}`} />
        </div>

        {/* Right panel: swaps between the pre-analysis panel and chat */}
        <div
          style={{ '--chat-w': `${chatWidth}px` } as React.CSSProperties}
          className="flex h-[80dvh] w-full min-w-0 flex-col overflow-hidden md:h-auto md:min-h-0 md:w-[var(--chat-w)] md:flex-shrink-0"
        >
          <Alert show={agentChecked && !agentHealth.reachable} tone="warning" live="status" className="mb-2 flex-shrink-0">
            The analyst box is not answering. Charts still work; ask again once it is back.
          </Alert>
          <FadeIn key={chatActive ? 'chat' : 'pre'} className="flex min-h-0 min-w-0 flex-1 flex-col">
          {chatActive ? (
            /* ── Chat mode ── */
            <AnalysisChat
              messages={messages}
              onSend={handleSend}
              onStop={handleStop}
              isAnalyzing={isAnalyzing}
              currentToolCall={currentToolCall}
              rateLimitCountdown={rateLimitCountdown}
              logId={logId}
              selectedSerials={selectedSerials}
              timeWindow={timeWindow}
              onFocusTimeChange={throttledSetFocusTime}
            />
          ) : (
            /* ── Pre-analysis mode ── */
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-surface-container-low">
              <PreAnalysisPanel
                logId={singleLogId}
                charts={logData.charts}
                chartsLoading={logData.chartsLoading}
                chartsError={logData.chartsError}
                report={logData.report}
                reportLoading={logData.reportLoading}
                onPickTime={throttledSetFocusTime}
              />

              {/* Prompt input bar at bottom */}
              <Reveal show={!!logId}>
                <div className="flex-shrink-0 bg-surface-container p-3">
                  <div className="flex min-w-0 gap-2">
                    <Field
                      type="text"
                      value={promptInput}
                      onChange={(e) => setPromptInput(e.target.value)}
                      placeholder="Ask about this flight, or press Analyze for a full read"
                      aria-label="Question for the analyst"
                      spellCheck={false}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const q = promptInput.trim();
                          setPromptInput('');
                          handleAnalyze(q || undefined);
                        }
                      }}
                    />
                    <Tooltip label="Runs a full analysis, or asks your question if you typed one (Enter)">
                      <button
                        type="button"
                        onClick={() => {
                          const q = promptInput.trim();
                          setPromptInput('');
                          handleAnalyze(q || undefined);
                        }}
                        disabled={isAnalyzing}
                        className={buttonClass('filled')}
                      >
                        Analyze
                      </button>
                    </Tooltip>
                  </div>
                  <Reveal show={!!timeWindow}>
                    <div className={`mt-2 text-xs text-on-surface-variant ${MONO} tabular-nums`}>
                      {timeWindow && <>Window {formatT(timeWindow.start)}
                      {timeWindow.end != null ? ` to ${formatT(timeWindow.end)}` : ' to end'}</>}
                    </div>
                  </Reveal>
                </div>
              </Reveal>
            </div>
          )}
          </FadeIn>
        </div>
      </div>

      {/* Session list dropdown */}
      {showSessionList && (
        <SessionListPanel
          api={api}
          onLoad={loadSavedSession}
          onClose={() => setShowSessionList(false)}
          currentSessionId={sessionId}
          anchor={sessionsKeyRef}
        />
      )}

      {/* Memories modal */}
      {showMemories && <MemoriesModal onClose={() => setShowMemories(false)} />}
    </div>
  );
};

export default AnalysisPage;
